import axios from 'axios';
import { ref } from 'vue';
import { resolveAsset } from './assets';

let defaultUserImage = null;
let maxAdImages = 6;
const AUTH_STORAGE_EVENT_KEY = 'auth_session_event';

const globalRouteLoading = ref(false);
const globalRouteLoadingText = ref('جاري التحميل...');
let navigationInProgress = false;
let navigationStartAt = 0;
let pendingNavRequests = 0;
let navigationEnded = false;
let acceptRequestsUntil = 0;
let hideTimer = null;
let forceStopTimer = null;

function clearHideTimer() {
  if (hideTimer) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
}

function clearForceStopTimer() {
  if (forceStopTimer) {
    clearTimeout(forceStopTimer);
    forceStopTimer = null;
  }
}

function scheduleHideIfReady() {
  if (!navigationInProgress) return;
  if (!navigationEnded) return;
  if (pendingNavRequests > 0) return;
  const elapsed = Date.now() - navigationStartAt;
  const wait = Math.max(0, 250 - elapsed);
  clearHideTimer();
  hideTimer = setTimeout(() => {
    pendingNavRequests = 0;
    navigationInProgress = false;
    navigationEnded = false;
    globalRouteLoading.value = false;
  }, wait);
}

export function startGlobalRouteLoading(text = 'جاري التحميل...') {
  clearHideTimer();
  clearForceStopTimer();
  navigationInProgress = true;
  navigationEnded = false;
  pendingNavRequests = 0;
  navigationStartAt = Date.now();
  acceptRequestsUntil = navigationStartAt + 2000;
  globalRouteLoadingText.value = text || 'جاري التحميل...';
  globalRouteLoading.value = true;
  forceStopTimer = setTimeout(() => {
    pendingNavRequests = 0;
    navigationInProgress = false;
    navigationEnded = false;
    globalRouteLoading.value = false;
  }, 15000);
}

export function endGlobalRouteLoading() {
  navigationEnded = true;
  acceptRequestsUntil = Math.max(acceptRequestsUntil, Date.now() + 2000);
  clearForceStopTimer();
  scheduleHideIfReady();
}

export function trackNavigationRequest(config) {
  if (!navigationInProgress) return;
  if (Date.now() > acceptRequestsUntil) return;
  const headers = config?.headers || {};
  const silent =
    headers['X-Silent-Loading'] === true ||
    headers['X-Silent-Loading'] === 'true' ||
    headers['X-No-Global-Loader'] === true ||
    headers['X-No-Global-Loader'] === 'true';
  if (silent) return;
  pendingNavRequests += 1;
  config.__navTracked = true;
}

export function untrackNavigationRequest(config) {
  if (!config?.__navTracked) return;
  config.__navTracked = false;
  pendingNavRequests = Math.max(0, pendingNavRequests - 1);
  scheduleHideIfReady();
}

export function getGlobalRouteLoadingState() {
  return { globalRouteLoading, globalRouteLoadingText };
}

export function getAuthToken() {
  try {
    return sessionStorage.getItem('token') || '';
  } catch (_) {
    return '';
  }
}

export function broadcastAuthState(reason = 'update', extra = {}) {
  const detail = {
    reason,
    hasToken: !!getAuthToken(),
    at: Date.now(),
    ...extra,
  };

  try {
    window.dispatchEvent(new CustomEvent('auth:changed', { detail }));
  } catch (_) {
    void 0;
  }

  try {
    localStorage.setItem(AUTH_STORAGE_EVENT_KEY, JSON.stringify(detail));
  } catch (_) {
    void 0;
  }
}

export function setAuthToken(token, axiosInstance, reason = 'login') {
  const value = String(token || '').trim();
  if (!value) return '';

  try { sessionStorage.setItem('token', value); } catch (_) { void 0; }
  try { sessionStorage.removeItem('home_cache_v1'); } catch (_) { void 0; }
  try { localStorage.removeItem('token'); } catch (_) { void 0; }

  try {
    if (axiosInstance?.defaults?.headers?.common) {
      axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${value}`;
    }
  } catch (_) { void 0; }

  broadcastAuthState(reason, { hasToken: true });
  return value;
}

export function migrateLegacyAuthToken(axiosInstance) {
  const sessionToken = getAuthToken();
  if (sessionToken) {
    try { localStorage.removeItem('token'); } catch (_) { void 0; }
    try {
      if (axiosInstance?.defaults?.headers?.common) {
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${sessionToken}`;
      }
    } catch (_) { void 0; }
    return sessionToken;
  }

  let legacyToken = '';
  try {
    legacyToken = String(localStorage.getItem('token') || '').trim();
  } catch (_) {
    legacyToken = '';
  }

  if (!legacyToken) return '';
  return setAuthToken(legacyToken, axiosInstance, 'legacy-migration');
}

export function fullLogoutWipe(axiosInstance) {
  const keysToRemove = [
    'token',
    'user',
    'IsLoggedIn',
    'home_cache_v1',
    'adsImages',
    'ad_name',
    'ad_price',
    'ad_category_id',
    'ad_city_id',
    'ad_details',
    'ad_contact_method',
    'ad_status',
    'banner_img',
    'banner_text',
    'banner_video',
    'bannerOrAd',
    'random_token',
    'adId',
    'ad_price',
    'preserve_ad_draft',
    'fd_img',
  ];

  try { sessionStorage.removeItem('token'); } catch (_) { void 0; }
  try { sessionStorage.removeItem('home_cache_v1'); } catch (_) { void 0; }

  for (const k of keysToRemove) {
    try { localStorage.removeItem(k); } catch (_) { void 0; }
  }

  try {
    if (axiosInstance?.defaults?.headers?.common) {
      delete axiosInstance.defaults.headers.common['Authorization'];
    }
  } catch (_) { void 0; }

  broadcastAuthState('logout', { hasToken: false });
}

export function getDefaultUserImage() {
  return defaultUserImage;
}

export function getMaxAdImages() {
  return maxAdImages;
}

export async function initDefaults(app) {
  // قيمة ابتدائية محلية آمنة إلى أن يصل رد الخادم
  try {
    defaultUserImage = require('../assets/imgs/user.png');
    app.config.globalProperties.$defaultUserImage = defaultUserImage;
  } catch (_) {
    app.config.globalProperties.$defaultUserImage = '';
  }

  const applyMaxAdImages = (raw) => {
    const parsedMax = parseInt(String(raw ?? ''), 10);
    if (!Number.isFinite(parsedMax) || parsedMax <= 0) return false;
    const changed = parsedMax !== maxAdImages;
    maxAdImages = parsedMax;
    app.config.globalProperties.$maxAdImages = maxAdImages;
    try { localStorage.setItem('max_ad_images', String(maxAdImages)); } catch (_) { void 0; }
    if (changed) {
      try { window.dispatchEvent(new Event('maxAdImages:updated')); } catch (_) { void 0; }
    }
    return true;
  };

  try {
    const storedMax = parseInt(localStorage.getItem('max_ad_images') || '', 10);
    if (Number.isFinite(storedMax) && storedMax > 0) {
      maxAdImages = storedMax;
    }
  } catch (_) { void 0; }
  app.config.globalProperties.$maxAdImages = maxAdImages;

  try {
    try {
      const raw = sessionStorage.getItem('home_cache_v1');
      if (raw) {
        const parsed = JSON.parse(raw);
        const data = parsed?.data;
        let used = false;
        const defUser = data?.default_user || data?.nav_and_footer_data?.default_user || '';
        const resolvedDef = defUser ? resolveAsset(defUser) : null;
        if (resolvedDef && !String(resolvedDef).includes('storage/images/settings/')) {
          defaultUserImage = resolvedDef;
          app.config.globalProperties.$defaultUserImage = defaultUserImage;
          used = true;
        }
        const rawMax =
          data?.max_ad_images ??
          data?.max_ads_images ??
          data?.max_advertisement_images ??
          data?.max_images ??
          data?.settings?.max_ad_images ??
          data?.nav_and_footer_data?.max_ad_images ??
          data?.nav_and_footer_data?.max_ads_images ??
          null;
        if (applyMaxAdImages(rawMax)) used = true;
        void used;
      }
    } catch (_) { void 0; }

    const res = await axios.get(`home?t=${Date.now()}`, { headers: { 'X-No-Auth-Redirect': true } });
    const data = res?.data?.data || {};
    const defUser = data.default_user || data.nav_and_footer_data?.default_user || '';
    const resolvedDef = defUser ? resolveAsset(defUser) : null;
    if (resolvedDef && !String(resolvedDef).includes('storage/images/settings/')) {
      defaultUserImage = resolvedDef;
      app.config.globalProperties.$defaultUserImage = defaultUserImage;
    }

    const rawMax =
      data.max_ad_images ??
      data.max_ads_images ??
      data.max_advertisement_images ??
      data.max_images ??
      data.settings?.max_ad_images ??
      data.nav_and_footer_data?.max_ad_images ??
      data.nav_and_footer_data?.max_ads_images ??
      null;
    applyMaxAdImages(rawMax);
  } catch (_) {
    // أبقِ على الصورة المحلية إن فشل الطلب
  }
}
